import { getCompanies } from '@lib/api_ts/companiesApi';
import { getGroupsByCompanyId } from '@lib/api_ts/groupsApi';
import { getNotices } from '@lib/api_ts/noticesApi';
import { ProjectType } from '@lib/api_ts/projectApi';
import { useQuery } from '@tanstack/react-query';
import { Dispatch, forwardRef, SetStateAction, useImperativeHandle } from 'react';
import { Form, Row } from 'react-bootstrap';
import RenderInput from './RenderInput';
import validateField from './utils/validateField';
import { PermissionsType } from '@components/permissions/PermissionWrapper';

export type FieldType = {
    name: string;
    label: string;
    type: 'text' | 'select' | 'date' | 'textarea' | 'radio';
    required?: boolean;
    isDisabled?: boolean;
    disablesWhenCompiled?: string;
    isMulti?: boolean;
    options?: { value: string; label: string; fieldName?: string }[];
    isLoading?: boolean;
    hidden?: boolean;
}

export interface RegistryFormHandle {
    validate: () => string[]; // ritorna i campi invalidi
}


interface RegistryFormProps {
    type: 'create' | 'edit';
    projectToSave: Partial<ProjectType> | undefined
    setProjectToSave: Dispatch<SetStateAction<Partial<ProjectType> | undefined>>
    // isSubmitting: boolean
    invalidFields: string[]
    setInvalidFields: Dispatch<SetStateAction<string[]>>
    isDirty: boolean;
    setIsDirty: Dispatch<SetStateAction<boolean>>;
    permissions: PermissionsType;
}

const RegistryForm = forwardRef<RegistryFormHandle, RegistryFormProps>(
    ({ type, projectToSave, setProjectToSave, /*isSubmitting,*/ invalidFields, setInvalidFields, isDirty, setIsDirty, permissions }, ref) => {
        const { data: companies, isPending: isPendingCompanies } = useQuery({
            queryKey: ["companies"],
            queryFn: getCompanies

        })
        const { data: notices, isPending: isPendingNotices } = useQuery({
            queryKey: ["notices"],
            queryFn: getNotices
        })
        const { data: groups, isLoading: isLoadingGroups, isFetching: isFetchingGroups, } = useQuery({
            queryKey: ["groups"],
            queryFn: () => getGroupsByCompanyId(projectToSave?.companyId),
            enabled: !!projectToSave?.companyId
        })

        const fieldRows = [
            {
                key: "row_0",
                fields: [{
                    label: 'Nome',
                    name: 'name',
                    type: "text" as const,
                    required: true,
                    isDisabled: !permissions?.canEditProjRegistry,
                }, {
                    label: 'Società',
                    name: 'companyId',
                    type: "select" as const,
                    options: companies?.map(company => ({
                        label: company.name,
                        value: String(company.id),
                        aiEnabled: company.aiEnabled,
                    })) || [],
                    isLoading: isPendingCompanies,
                    required: true,
                    isDisabled: !permissions?.canEditProjRegistry,
                },
                ...type === 'edit' ? [{
                    label: projectToSave.cupType === "C" ? "CUP" : "ID Progetto",
                    name: "cup",
                    type: "text" as const,
                    required: true,
                    isDisabled: true //!permissions?.canEditProjRegistry
                }] : []
                ]
            },

            ...type === 'create' ? [{
                key: "row_1",
                fields: [
                    {
                        label: "Scegli se inserire il CUP oppure l'ID Progetto:",
                        name: "cupType",
                        type: "radio" as const,
                        options: [
                            { label: "CUP", value: "C", fieldName: "cup" },
                            { label: "ID Progetto", value: "I", fieldName: "cup" }
                        ],
                        required: true,
                        isDisabled: !permissions?.canEditProjRegistry,
                    },
                    {
                        label: "Valore CUP / ID Progetto",
                        name: "cup",
                        type: "text" as const,
                        required: true,
                        hidden: true // se non vuoi mostrarlo nel form ma vuoi che sia validato
                    }
                    // {
                    //     label: "CUP",
                    //     name: "temp_cup",
                    //     type: "text" as const,
                    //     required: true,
                    //     // disablesWhenCompiled: "temp_projectId",

                    // }, {
                    //     label: "ID Progettto",
                    //     name: "temp_projectId",
                    //     type: "text" as const,
                    //     required: true,
                    //     // disablesWhenCompiled: "temp_cup",
                    // }
                ]
            }]
                : [],

            {
                key: "row_2",
                fields: [{
                    label: "Gruppi",
                    name: "groupids",
                    type: "select" as const,
                    options: groups?.map(group => ({
                        label: group.name,
                        value: String(group.id),
                    })) || [],
                    isMulti: true,
                    required: true,
                    isDisabled: !projectToSave || !projectToSave.companyId || !permissions?.canEditProjRegistry,
                    isLoading: isLoadingGroups || isFetchingGroups,

                }, {
                    label: "Bando",
                    name: "noticeId",
                    type: "select" as const,
                    options: notices?.map(notice => ({
                        label: notice.call,
                        value: String(notice.id),
                        fund: notice.fund.code,
                        streamConfigurationType: notice.streamConfigurationType
                    })) || [],
                    required: true,
                    isLoading: isPendingNotices,
                    isDisabled: !permissions?.canEditProjRegistry,
                }]
            },
            {
                key: "row_3",
                fields: [
                    {
                        label: "Responsabile di progetto",
                        name: "manager",
                        type: "text" as const,
                        isDisabled: !permissions?.canEditProjRegistry,
                    },
                    {
                        label: "Project Manager",
                        name: "oeManager",
                        type: "text" as const,
                        isDisabled: !permissions?.canEditProjRegistry,
                    }
                    ,]
            },
            {
                key: "row_4 ",
                fields: [{
                    label: "Avvio previsto",
                    name: "forecastStartDate",
                    type: "date" as const,
                    required: true,
                    isDisabled: !permissions?.canEditProjRegistry,
                }, {
                    label: "Fine prevista",
                    name: "forecastEndDate",
                    type: "date" as const,
                    required: true,
                    isDisabled: !permissions?.canEditProjRegistry,
                }, {
                    label: "Avvio effettivo",
                    name: "startDate",
                    type: "date" as const,
                    required: false,
                    isDisabled: !permissions?.canEditProjRegistry,
                }, {
                    label: "Fine effettiva",
                    name: "endDate",
                    type: "date" as const,
                    required: false,
                    isDisabled: !permissions?.canEditProjRegistry,
                }]
            },

            {
                key: "row_5",
                fields: [{
                    label: "Descrizione",
                    name: "description",
                    type: "textarea" as const,
                    required: false,
                    isDisabled: !permissions?.canEditProjRegistry,
                }]
            },
            {
                key: "row_6",
                fields: [{
                    label: "Note",
                    name: "note",
                    type: "textarea" as const,
                    required: false,
                    isDisabled: !permissions?.canEditProjRegistry,
                }]
            },
        ]
        useImperativeHandle(ref, () => ({
            validate: () => {
                // if (!projectToSave) return [];
                const invalids: string[] = [];

                fieldRows.forEach(row => {
                    row.fields.forEach((field: FieldType) => {
                        const value = projectToSave?.[field.name];
                        const error = validateField(field, value, projectToSave);

                        if (!error) return
                        invalids.push(field.name);
                    });
                });

                setInvalidFields(invalids);
                return invalids;
            }
        }));

        return <Form>
            {fieldRows.map(fieldRow => <Row style={{ marginBottom: '40px' }} key={fieldRow.key}>
                {fieldRow.fields.map((field: FieldType) => {
                    return <RenderInput
                        key={field.name}
                        field={field}
                        projectToSave={projectToSave}
                        // isSubmitting={isSubmitting}
                        setProjectToSave={setProjectToSave}
                        getIsInvalid={(customFieldName?: string) => invalidFields.includes(customFieldName || field.name)}
                        // invalidFields={invalidFields}
                        setInvalidFields={setInvalidFields}
                        isDirty={isDirty}
                        setIsDirty={setIsDirty}
                    />
                }
                )}

            </Row>)}

        </Form>
    })

export default RegistryForm